import type { AuditVertical, ChannelBenchmark } from '../../../types/audit';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import StatusPill from './StatusPill';
import { toneFor } from './tone';

interface ChannelBenchmarksTableProps {
  channels: ChannelBenchmark[] | Record<string, unknown>;
  vertical?: AuditVertical;
}

function normalizeChannels(input: ChannelBenchmark[] | Record<string, unknown>): ChannelBenchmark[] {
  if (Array.isArray(input)) return input;
  // API may return {metrics: [...]} or {channels: [...]} instead of a flat array
  if (input && typeof input === 'object') {
    for (const key of ['metrics', 'channels', 'data']) {
      const val = (input as Record<string, unknown>)[key];
      if (Array.isArray(val)) return val as ChannelBenchmark[];
    }
  }
  return [];
}

const TH = 'bg-muted/40 font-mono text-[10px] font-semibold uppercase tracking-[0.07em] text-muted-foreground whitespace-nowrap';
const NUM = 'font-mono text-[13px] tabular-nums whitespace-nowrap';

export default function ChannelBenchmarksTable({ channels, vertical }: ChannelBenchmarksTableProps) {
  const rows = normalizeChannels(channels);
  if (rows.length === 0) {
    return <p className="text-muted-foreground text-[13px]">No channel benchmark data available.</p>;
  }

  // "Trial Rate"/"LTV Multiple" are subscription concepts (free trial → paid) —
  // meaningless for a store's order channels. E-commerce/hybrid reports show
  // AOV + revenue share per channel instead; see ChannelBenchmark.aov/
  // revenueSharePct (src/tools/contrib/marketing/compute_ecommerce_kpis.py's
  // by_channel feeds these).
  const isEcommerce = vertical === 'ecommerce' || vertical === 'generic';
  // B2B reports (LinkedIn lead gen) have neither trials nor orders — the
  // per-channel efficiency read is Cost per Lead / Cost per Qualified Lead.
  const isB2B = vertical === 'b2b';

  return (
    <div className="overflow-x-auto">
      <Table className="min-w-[560px]">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            <TableHead className={TH}>Channel</TableHead>
            <TableHead className={TH}>CPA</TableHead>
            {isB2B ? (
              <>
                <TableHead className={TH}>Cost per lead</TableHead>
                <TableHead className={TH}>Cost per qualified lead</TableHead>
              </>
            ) : isEcommerce ? (
              <>
                <TableHead className={TH}>CPO</TableHead>
                <TableHead className={TH}>AOV</TableHead>
                <TableHead className={TH}>Revenue share</TableHead>
              </>
            ) : (
              <>
                <TableHead className={TH}>Trial rate</TableHead>
                <TableHead className={TH}>LTV multiple</TableHead>
              </>
            )}
            <TableHead className={cn(TH, 'text-right')}>Verdict</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((ch) => (
            <TableRow key={ch.channel}>
              <TableCell className="font-semibold text-foreground">{ch.channel}</TableCell>
              {/* null/blank CPA (organic channel, or no ad-spend reconciliation
                  yet) must read as a dash — same as CPO — never an empty cell. */}
              <TableCell className={NUM}>{ch.cpa ?? '-'}</TableCell>
              {isB2B ? (
                <>
                  <TableCell className={NUM}>{ch.costPerLead ?? '-'}</TableCell>
                  <TableCell className={NUM}>{ch.costPerQualifiedLead ?? '-'}</TableCell>
                </>
              ) : isEcommerce ? (
                <>
                  <TableCell className={NUM}>{ch.cpo ?? '-'}</TableCell>
                  <TableCell className={NUM}>{ch.aov ?? '-'}</TableCell>
                  <TableCell className={NUM}>{ch.revenueSharePct ?? '-'}</TableCell>
                </>
              ) : (
                <>
                  <TableCell className={NUM}>{ch.trialRate}</TableCell>
                  <TableCell className={NUM}>{ch.ltvMultiple}</TableCell>
                </>
              )}
              <TableCell className="text-right">
                <StatusPill tone={toneFor(ch.verdict)}>{ch.verdict}</StatusPill>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}
